import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { inr, formatDateTime } from "@/lib/format";
import { History, Info, Eye, FileSpreadsheet } from "lucide-react";

const META_KEY = "ls_import_batches";

function readMeta() {
  try { return JSON.parse(localStorage.getItem(META_KEY) || "[]"); } catch { return []; }
}

export default function ImportHistory() {
  const [viewBatch, setViewBatch] = useState(null);
  const { data: leads = [], isLoading } = useQuery({ queryKey: ["leads-for-history"], queryFn: async () => (await api.get("/leads")).data });

  const batches = useMemo(() => {
    const meta = readMeta();
    const metaById = Object.fromEntries(meta.map((m) => [m.batch_id, m]));
    const groups = {};
    leads.forEach((l) => {
      const bid = l.upload_batch_id;
      if (!bid) return;
      (groups[bid] ||= []).push(l);
    });
    // batches present in recent leads
    const rows = Object.entries(groups).map(([bid, ls]) => {
      const dates = ls.map((l) => l.created_at).filter(Boolean).sort();
      return {
        batch_id: bid,
        recentCount: ls.length,
        firstCreated: dates[0],
        lastCreated: dates[dates.length - 1],
        loanTypes: [...new Set(ls.map((l) => l.loan_type).filter(Boolean))],
        assignees: [...new Set(ls.map((l) => l.assigned_to_name).filter(Boolean))],
        filename: metaById[bid]?.filename,
        createdMeta: metaById[bid]?.created,
        skipped: metaById[bid]?.skipped,
        at: metaById[bid]?.at,
        leads: ls,
      };
    });
    // web-import batches not present in the recent window (older / all assigned elsewhere)
    meta.forEach((m) => {
      if (!groups[m.batch_id]) rows.push({ batch_id: m.batch_id, recentCount: 0, filename: m.filename, createdMeta: m.created, skipped: m.skipped, at: m.at, loanTypes: [], assignees: [], leads: [] });
    });
    return rows.sort((a, b) => new Date(b.at || b.lastCreated || 0) - new Date(a.at || a.lastCreated || 0));
  }, [leads]);

  return (
    <div data-testid="import-history-page">
      <PageHeader title="Import History" subtitle="Bulk upload batches reconstructed from your most recent leads." />

      <div className="flex items-start gap-2 text-sm text-slate-500 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 rounded-lg p-3 mb-5">
        <Info className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
        <span>The backend does not support deleting or undoing an import, so batches here are <span className="font-medium">review-only</span>. Older batches beyond the recent-leads window may not appear; batches uploaded from this browser always show their filename.</span>
      </div>

      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden p-0">
        <Table data-testid="import-history-table">
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50">
              <TableHead className="uppercase text-xs tracking-wider">Batch</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-right">Leads</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Loan Types</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Assignees</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Uploaded</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 4 }).map((_, i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>)}
            {!isLoading && batches.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10"><History className="h-8 w-8 mx-auto mb-2 opacity-40" />No bulk imports found yet.</TableCell></TableRow>}
            {!isLoading && batches.map((b) => (
              <TableRow key={b.batch_id} data-testid={`batch-row-${b.batch_id}`}>
                <TableCell>
                  <div className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-1.5">{b.filename ? <><FileSpreadsheet className="h-3.5 w-3.5 text-blue-600" />{b.filename}</> : "Bulk upload"}</div>
                  <div className="text-[11px] text-slate-400 font-num">{b.batch_id.slice(0, 8)}…</div>
                </TableCell>
                <TableCell className="text-right font-num font-semibold">{b.createdMeta ?? b.recentCount}{b.createdMeta && b.recentCount !== b.createdMeta ? <span className="text-xs text-slate-400"> ({b.recentCount} recent)</span> : ""}</TableCell>
                <TableCell><div className="flex flex-wrap gap-1">{b.loanTypes.slice(0, 3).map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}{b.loanTypes.length === 0 && "—"}</div></TableCell>
                <TableCell className="text-slate-600 dark:text-slate-300 text-sm">{b.assignees.slice(0, 2).join(", ") || "—"}{b.assignees.length > 2 ? ` +${b.assignees.length - 2}` : ""}</TableCell>
                <TableCell className="text-slate-500 text-sm">{formatDateTime(b.at || b.lastCreated)}</TableCell>
                <TableCell className="text-right"><Button variant="outline" size="sm" className="h-8" disabled={b.leads.length === 0} onClick={() => setViewBatch(b)} data-testid={`view-batch-${b.batch_id}`}><Eye className="h-3.5 w-3.5 mr-1" />View</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!viewBatch} onOpenChange={(o) => !o && setViewBatch(null)}>
        <DialogContent className="max-w-3xl bg-white dark:bg-slate-900" data-testid="batch-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading">{viewBatch?.filename || "Bulk upload"}</DialogTitle><DialogDescription>{viewBatch?.leads?.length} leads from this batch (within the recent window).</DialogDescription></DialogHeader>
          <div className="max-h-[60vh] overflow-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50"><TableHead className="text-xs uppercase">Name</TableHead><TableHead className="text-xs uppercase">Phone</TableHead><TableHead className="text-xs uppercase">Loan Type</TableHead><TableHead className="text-xs uppercase text-right">Amount</TableHead><TableHead className="text-xs uppercase">Assigned</TableHead><TableHead className="text-xs uppercase">Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {viewBatch?.leads?.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="font-medium">{l.name || "—"}</TableCell>
                    <TableCell className="font-num text-slate-600 dark:text-slate-300">{l.phone || "—"}</TableCell>
                    <TableCell className="text-slate-600 dark:text-slate-300">{l.loan_type || "—"}</TableCell>
                    <TableCell className="text-right font-num">{inr(l.loan_amount)}</TableCell>
                    <TableCell className="text-slate-600 dark:text-slate-300">{l.assigned_to_name || "—"}</TableCell>
                    <TableCell><StatusBadge status={l.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
